import React, { memo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';

import sectionGreyBg from '../../assets/images/section-grey.webp';
import sectionGreyBgPattern from '../../assets/images/bg_pattern.svg';
import useProfileData from '../../hooks/useProfileData';
import SectionHeader from '../../components/resume/SectionHeader';
import EducationSection from '../../components/resume/EducationSection';
import ExperienceSection from '../../components/resume/ExperienceSection';
import SkillsSection from '../../components/resume/SkillsSection';

/**
 * Resume Section Component
 * Displays education, experience, and skills information
 * Optimized with custom hooks and memoized components
 */
const ResumeSection = memo(() => {
    const { data, loading, errors } = useProfileData();

    return (
        <section
            id="resume"
            className="section"
            style={{ backgroundImage: `url(${sectionGreyBgPattern})` }}
            aria-labelledby="resume-heading"
        >
            {/* Top Decoration */}
            <div
                className="section-decoration top"
                style={{ backgroundImage: `url(${sectionGreyBg})`, height: '35px' }}
                aria-hidden="true"
            />

            <Container>
                {/* Section Header */}
                <SectionHeader 
                    title="Summary" 
                    subtitle="Resume"
                />

                {/* Education & Experience */}
                <Row className="g-5 mt-2">
                    <Col lg={6} md={12} className="wow fadeInUp">
                        <h3 className="text-6 fw-500 mb-4 pb-2 text-dark" id="education-heading">
                            My Education
                        </h3>
                        <div 
                            className="border-start border-2 border-primary ps-3"
                            role="region"
                            aria-labelledby="education-heading"
                        >
                            <EducationSection
                                educations={data.educations}
                                isLoading={loading.educations}
                                error={errors.educations}
                            />
                        </div>
                    </Col>

                    <Col lg={6} md={12} className="wow fadeInUp" data-wow-delay="0.2s">
                        <h3 className="text-6 fw-500 mb-4 pb-2 text-dark" id="experience-heading">
                            My Experience
                        </h3>
                        <div 
                            className="border-start border-2 border-primary ps-3"
                            role="region"
                            aria-labelledby="experience-heading"
                        >
                            <ExperienceSection
                                experiences={data.experiences}
                                isLoading={loading.experiences}
                                error={errors.experiences}
                            />
                        </div>
                    </Col>
                </Row>

                {/* Skills */}
                <section aria-labelledby="skills-heading">
                    <h2 className="text-6 fw-500 mb-4 pb-2 mt-5 wow fadeInUp" id="skills-heading">
                        My Skills
                    </h2>
                    <Row className="gx-5">
                        <SkillsSection
                            skills={data.skills}
                            isLoading={loading.skills}
                            error={errors.skills}
                        />
                    </Row>
                </section>
            </Container>
        </section>
    );
});

ResumeSection.displayName = 'ResumeSection';

export default ResumeSection;
